import React, { useEffect } from 'react';
import { Box } from '@mui/material';

import TrendDetails from '../../analytics/TrendDetails';
import { createBitrateCalculator } from '../services/calculateBitrate';

export type WebmetricsParams = {
  toggleStatsVisibility: boolean;
  statsReport: RTCStatsReport;
  sx?: any;
  isFullScreen?: boolean;
};

const TrendStyle = {
  width: 210,
  height: 140,
  opacity: 0.5,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '0px',
};

const WebrtcMetrics = ({
  toggleStatsVisibility,
  statsReport,
  sx,
  isFullScreen,
}: WebmetricsParams) => {
  const [jitter, setJitter] = React.useState(0);
  const [fps, setFps] = React.useState(0);
  const [currentBitrate, setBitrate] = React.useState(0);
  const [jitterHistory, setJitterHistory] = React.useState<any[]>([]);
  const [fpsHistory, setFPSHistory] = React.useState<any[]>([]);
  const [bitrateHistory, setBitrateHistory] = React.useState<any[]>([]);
  const [framesDroppedHistory, setFramesDroppedHistory] = React.useState<any[]>([]);
  const [framesDropped, setFramesDropped] = React.useState(0);

  // Isolated per-mount bitrate calculator (see createBitrateCalculator) so the chart's
  // bytesReceived deltas never collide with the headless analytics collector's.
  const calcBitrateRef = React.useRef(createBitrateCalculator());

  const updateArray = React.useCallback((
    data: number,
    setArray: React.Dispatch<React.SetStateAction<any[]>>,
    maxLength: number,
  ) => {
    setArray((prevArray: number[]) => {
      const newArray = [...prevArray, data];
      if (newArray.length > maxLength) {
        newArray.shift();
      }
      return newArray;
    });
  }, []);

  useEffect(() => {
    if (!statsReport || !statsReport.forEach) return;
    statsReport.forEach((report) => {
      if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
        const bitrate = calcBitrateRef.current(report);
        let calculatedBitrate = Number.parseFloat((bitrate / 1000000).toFixed(2));
        calculatedBitrate = isNaN(calculatedBitrate) ? 0 : calculatedBitrate;
        updateArray(calculatedBitrate, setBitrateHistory, 120);
        setBitrate(calculatedBitrate);
      }

      Object.keys(report).forEach((key) => {
        if (key === 'jitter') {
          let jitterValue = report[key];
          if (isNaN(jitterValue)) {
            jitterValue = 0;
          }
          updateArray(jitterValue, setJitterHistory, 120);
          setJitter(jitterValue);
        }
        if (key === 'framesPerSecond') {
          let fps = report[key];
          if (isNaN(fps)) {
            fps = 0;
          }
          updateArray(fps, setFPSHistory, 120);
          setFps(fps);
        }
        if (key === 'framesDropped') {
          let framesDropped = report[key];
          if (isNaN(framesDropped)) {
            framesDropped = 0;
          }
          setFramesDropped(framesDropped);
          updateArray(framesDropped, setFramesDroppedHistory, 120);
        }
      });
    });
  }, [statsReport, updateArray]);

  return (
    <Box
      sx={{
        visibility: toggleStatsVisibility ? 'visible' : 'hidden',
        position: 'absolute',
        top: isFullScreen ? 80 : 0,
        right: 0,
        aspectRatio: '16/9',
        height: 140,
        display: 'flex',
        direction: 'rtl',
        backdropFilter: 'blur(10px)',
        ...sx,
      }}
    >
      <TrendDetails
        style={TrendStyle}
        title="Jitter"
        data={jitterHistory}
        value={jitter}
        limit={120}
      />
      <TrendDetails style={TrendStyle} title="Fps" limit={120} data={fpsHistory} value={fps} />
      <TrendDetails
        style={TrendStyle}
        title="Frames Dropped"
        limit={120}
        data={framesDroppedHistory}
        value={framesDropped}
      />
      <TrendDetails
        style={TrendStyle}
        title="Bitrate"
        limit={120}
        data={bitrateHistory}
        value={`${currentBitrate}Mbps`}
      />
    </Box>
  );
};

export default WebrtcMetrics;
